<template>
    <div v-if="visible" class="copy-container">
        <div class="copy-content" ref="target">
            <ButtonSecondary
                size="s"
                :onClick="onCopy"
                iconPosition="left"
                icon="Wand"
                class="copy-content-button">
                {{ t({ key: 'copy.hotel.copy_to_all' }) }}
            </ButtonSecondary>

            <Toast v-if="message" icon="Check" color="green" padding="m">
                {{ message }}
            </Toast>
        </div>

        <Modal v-if="modal" :onClose="() => onSetModal(false)" size="s">
            <div class="copy-modal">
                <div class="copy-modal-content">
                    <p class="copy-modal-title">
                        {{ t({ key: 'copy.hotel.copy_details' }) }}
                    </p>
                    <p class="copy-modal-description">
                        {{ t({ key: 'copy.hotel.edit_later' }) }}
                    </p>
                    <p v-if="hasSpecialRequest" class="copy-modal-description">
                        {{ t({ key: 'copy.hotel.note_special_request' }) }}
                    </p>
                </div>

                <div class="copy-modal-buttons">
                    <ButtonPrimary :onClick="onApply">
                        {{ t({ key: 'copy.hotel.yes_copy' }) }}
                    </ButtonPrimary>
                    <ButtonText :onClick="() => onSetModal(false)">
                        {{ t({ key: 'cancellation.cancel' }) }}
                    </ButtonText>
                </div>
            </div>
        </Modal>
    </div>
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { Cart, HotelProduct, ProductType } from '@/api/Cart/cart.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { copyHotels, showCopyHotel } from '../Copy.helpers';
    import { useStore } from '@/store';
    import { submitFormById } from '@/helpers/formRegistry';
    import { useElementVisibility } from '@vueuse/core';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonText from '@ui/components/Button/ButtonText.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Toast from '@ui/components/Toast/Toast.vue';
    import {
        logStatsigCheckoutCopyFeatureApplied,
        logStatsigCheckoutCopyFeatureClick,
        logStatsigCheckoutCopyFeatureInvalidForm,
    } from '@/tracking/statsig/checkout.functions';

    export default defineComponent({
        name: 'CopyHotel',
        methods: { t },
        components: {
            ButtonPrimary,
            ButtonSecondary,
            ButtonText,
            Modal,
            Toast,
        },
        props: {
            product: { type: Object as PropType<HotelProduct>, required: true },
            formId: { type: String, required: true },
            formValid: { type: Boolean, required: true },
            formValues: { type: Object as PropType<FormValues | null> },
        },
        setup(props) {
            const modal = ref<boolean>(false);
            const message = ref<string | null>(null);

            const store = useStore();

            const target = ref(null);
            const targetIsVisible = useElementVisibility(target);

            const cart = computed<Cart>(() => store.state.cart.data);

            const visible = computed<boolean>(() =>
                showCopyHotel(props.product.Id, cart.value)
            );

            const hasSpecialRequest = computed<boolean>(() => {
                return props.product.Rooms.some((x) => x.Pax.SpecialRequest);
            });

            function onSetModal(state: boolean) {
                modal.value = state;
            }

            function onCopy() {
                logStatsigCheckoutCopyFeatureClick(ProductType.Hotel, cart.value);

                if (props.formValid) {
                    onSetModal(true);
                } else {
                    submitFormById(props.formId, true);
                    logStatsigCheckoutCopyFeatureInvalidForm(ProductType.Hotel, cart.value);
                }
            }

            function onApply() {
                const res = copyHotels(props.product.Id);

                if (res) {
                    message.value = t({
                        key: 'copy.hotel.copied_to_all_hotels',
                    });

                    onSetModal(false);

                    logStatsigCheckoutCopyFeatureApplied(ProductType.Hotel, cart.value);
                }
            }

            watch(targetIsVisible, (visible) => {
                if (message.value && !visible) {
                    message.value = null;
                }
            });

            watch(
                () => props.formValues,
                () => {
                    if (message.value) {
                        message.value = null;
                    }
                },
                { immediate: true, deep: true }
            );

            return {
                target,
                visible,
                message,
                modal,
                onSetModal,
                onApply,
                onCopy,
                hasSpecialRequest,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward '../Copy.scss';
</style>
